import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartRecommendationCardsBlockConfig } from '@front-platform/content-builder';
import {
  cartRecommendationCardsBlockResolveConfig,
  recommendationItemsForDisplay,
} from '@front-platform/content-builder';
import { Component, Inject, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { suggestionProductOrigin } from '@core/models/suggestion-channel.util';

import { DoodProductModel } from '@core/models/product.model';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product';
import { Store } from '@ngrx/store';
import { RecommendationItem } from '@core/services/api/recommendations/recommendations-api.service';

import {
  CART_RECOMMENDATION_CARDS_BLOCK_DATA_SOURCE,
  CartRecommendationCardsBlockDataSource,
} from './cart-recommendation-cards-block-view-model';
import { provideCartRecommendationCardsBlockDataSource } from './cart-recommendation-cards-block-data-source.providers';

export interface CartRecommendationCardsBlockRuntimeConfig {}

export type CartRecommendationCardsBlockConfigInput = Partial<
  Omit<CartRecommendationCardsBlockConfig, keyof CartRecommendationCardsBlockRuntimeConfig> &
    CartRecommendationCardsBlockRuntimeConfig
>;
export type CartRecommendationCardsBlockResolvedConfig = Omit<
  CartRecommendationCardsBlockConfig,
  keyof CartRecommendationCardsBlockRuntimeConfig
> &
  Partial<CartRecommendationCardsBlockRuntimeConfig>;
export interface CartRecommendationCardsBlockContract extends ConfigComponentContract<
  CartRecommendationCardsBlockConfigInput,
  CartRecommendationCardsBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-recommendation-cards-block',
  templateUrl: './cart-recommendation-cards-block.component.html',
  standalone: false,
  providers: [...provideCartRecommendationCardsBlockDataSource()],
})
export class CartRecommendationCardsBlockComponent implements CartRecommendationCardsBlockContract {
  readonly config = input<CartRecommendationCardsBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartRecommendationCardsBlockConfig,
    CartRecommendationCardsBlockRuntimeConfig
  >(cartRecommendationCardsBlockResolveConfig, () => ({ config: this.config() }));

  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly settingsSelector = inject(SettingsStoreSelector);

  readonly heading = computed(() => this.resolvedConfig()['heading'] as unknown as string);
  readonly leading = computed(() => this.resolvedConfig()['leading'] as unknown as string);
  readonly minRecommendations = computed(
    () => this.resolvedConfig()['min_recommendations'] as unknown,
  );
  readonly maxRecommendations = computed(
    () => this.resolvedConfig()['max_recommendations'] as unknown,
  );

  protected readonly isMobile = this.settingsSelector.deviceIsMobileScreen;
  protected readonly recommendationsVm$;

  protected currentIndex = 0;
  protected readonly nbDisplayCards = 5;

  constructor(
    @Inject(CART_RECOMMENDATION_CARDS_BLOCK_DATA_SOURCE)
    blockDataSource: CartRecommendationCardsBlockDataSource,
  ) {
    this.recommendationsVm$ = blockDataSource.vm$;
  }

  productClick(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: suggestionProductOrigin(
          this.router.url,
          'suggestion_set_cart-recommendation',
        ),
      }),
    );
  }

  productOf(recommendation: RecommendationItem): DoodProductModel {
    return recommendation.data as DoodProductModel;
  }

  protected recommendationsForDisplay(
    recommendations: readonly RecommendationItem[],
  ): RecommendationItem[] {
    return [
      ...recommendationItemsForDisplay(
        recommendations,
        this.minRecommendations(),
        this.maxRecommendations(),
      ),
    ];
  }

  protected prev(): void {
    this.currentIndex = this.currentIndex === 0 ? this.currentIndex : this.currentIndex - 1;
  }

  protected next(): void {
    this.currentIndex = this.currentIndex + 1;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartRecommendationCardsBlockComponent, {
  'keyAliases': {},
});
